<template>
  <nav class="table-pagination" aria-label="Page navigation">
    <button
      type="button"
      class="tp-btn tp-nav"
      :disabled="currentPage <= 1"
      aria-label="First page"
      title="First page"
      @click="goToPage(1)"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8.5 4 4.5 8l4 4M12 4 8 8l4 4" /></svg>
    </button>
    <button
      type="button"
      class="tp-btn tp-nav"
      :disabled="currentPage <= 1"
      aria-label="Previous page"
      title="Previous page"
      @click="goToPage(currentPage - 1)"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 4 6 8l4 4" /></svg>
    </button>

    <template v-for="(item, index) in pageItems" :key="item === '…' ? `gap-${index}` : item">
      <span v-if="item === '…'" class="tp-gap" aria-hidden="true">…</span>
      <button
        v-else
        type="button"
        :class="['tp-btn', 'tp-page', { 'tp-active': item === currentPage }]"
        :aria-current="item === currentPage ? 'page' : null"
        :aria-label="`Page ${item}`"
        @click="goToPage(item)"
      >
        {{ item }}
      </button>
    </template>

    <button
      type="button"
      class="tp-btn tp-nav"
      :disabled="currentPage >= totalPages"
      aria-label="Next page"
      title="Next page"
      @click="goToPage(currentPage + 1)"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 4 4 4-4 4" /></svg>
    </button>
    <button
      type="button"
      class="tp-btn tp-nav"
      :disabled="currentPage >= totalPages"
      aria-label="Last page"
      title="Last page"
      @click="goToPage(totalPages)"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 4 4 4-4 4M7.5 4l4 4-4 4" /></svg>
    </button>
  </nav>
</template>

<script>
export default {
  props: {
    currentPage: { type: Number, required: true },
    totalPages: { type: Number, required: true }
  },
  computed: {
    // Always shows first and last page, a window around the current page,
    // and an ellipsis for any skipped range, e.g. 1 … 4 5 6 … 12
    pageItems() {
      const total = Math.max(1, this.totalPages);
      const current = Math.min(Math.max(1, this.currentPage), total);
      const siblings = 1;

      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      let start = Math.max(2, current - siblings);
      let end = Math.min(total - 1, current + siblings);

      // Keep the number of visible slots constant near the edges
      if (current <= 3) end = 5;
      if (current >= total - 2) start = total - 4;

      const items = [1];
      if (start > 2) items.push('…');
      for (let p = start; p <= end; p++) items.push(p);
      if (end < total - 1) items.push('…');
      items.push(total);
      return items;
    }
  },
  watch: {
    // If the data shrinks (e.g. a filter is applied) and the current page no
    // longer exists, move the parent back to the last valid page
    totalPages(newTotal) {
      if (newTotal >= 1 && this.currentPage > newTotal) {
        this.$emit('page-change', newTotal);
      }
    }
  },
  methods: {
    goToPage(page) {
      const target = Math.min(Math.max(1, page), this.totalPages);
      if (target !== this.currentPage) this.$emit('page-change', target);
    }
  }
};
</script>

<style scoped>
.table-pagination {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2px;
  margin: 15px 0;
  padding: 3px;
  background-color: #fff;
  border: 1px solid #e2e5e9;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.tp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: none;
  border-radius: 7px;
  background-color: transparent;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.tp-btn:hover:not(:disabled):not(.tp-active) {
  background-color: #f1f4f8;
  color: #111827;
}

.tp-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.3);
}

.tp-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tp-active {
  background-color: #007bff;
  color: #fff;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 123, 255, 0.35);
  cursor: default;
}

.tp-nav {
  padding: 0;
  color: #6b7280;
}

/* Small extra gap between the arrow controls and the page numbers */
.tp-nav:nth-of-type(2) {
  margin-right: 3px;
}

.tp-nav:nth-last-of-type(2) {
  margin-left: 3px;
}

.tp-nav svg {
  width: 14px;
  height: 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tp-gap {
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
  min-width: 16px;
  height: 30px;
  padding-bottom: 8px;
  color: #9ca3af;
  font-size: 13px;
  user-select: none;
  box-sizing: border-box;
}
</style>
